import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils.js';
import { Card } from '../ui/card.js';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import { 
  Search, 
  ChevronUp, 
  ChevronDown, 
  ChevronsUpDown, 
  Loader2,
  Download,
  Share,
  Play,
  Plus,
  Copy,
  Edit3,
  X,
  Calculator,
  History,
  AlertCircle,
  Brain,
  Zap,
  BarChart3,
  Settings,
  Users,
  GitBranch,
  Database
} from 'lucide-react';

interface GridRow {
  id: string;
  [key: string]: any;
}

interface GridColumn {
  id: string;
  key: string;
  header: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'formula';
  width?: string;
  sortable?: boolean;
  editable?: boolean;
  required?: boolean;
}

// Enhanced interfaces for world-class SmartGrid
interface CellReference {
  sheet: string;
  column: number;
  row: number;
  absolute: boolean;
}

interface FormulaDefinition {
  expression: string;
  dependencies: CellReference[];
  result: any;
  lastEvaluated: string;
}

interface DataValidation {
  type: 'list' | 'number' | 'date' | 'custom';
  criteria: any;
  errorMessage?: string;
}

interface ConditionalFormat {
  condition: FormulaDefinition;
  style: CellStyle;
  priority: number;
}

interface CellStyle {
  backgroundColor?: string;
  textColor?: string;
  fontWeight?: string;
  borderColor?: string;
  borderStyle?: string;
}

interface SmartGridSheet {
  id: string;
  name: string;
  rows: GridRow[];
  columns: GridColumn[];
  formulas: Map<string, FormulaDefinition>;
  validations: Map<string, DataValidation>;
  conditionalFormats: ConditionalFormat[];
  metadata: SheetMetadata;
}

interface SheetMetadata {
  entity: string;
  lastModified: string;
  version: number;
  owner: string;
  collaborators: string[];
  permissions: Permission[];
}

interface Permission {
  userId: string;
  role: 'viewer' | 'editor' | 'admin';
  grantedAt: string;
}

interface SmartGridWorkbook {
  sheets: Map<string, SmartGridSheet>;
  activeSheet: string;
  sharedFormulas: Map<string, FormulaDefinition>;
  crossSheetReferences: CellReference[];
  version: number;
  lastModified: string;
}

interface DataInsights {
  anomalies: AnomalyReport[];
  trends: TrendPrediction[];
  suggestions: InsightSuggestion[];
  correlations: CorrelationAnalysis[];
}

interface AnomalyReport {
  cellReference: string;
  value: any;
  expectedRange: [number, number];
  confidence: number;
  description: string;
}

interface TrendPrediction {
  column: string;
  nextValues: any[];
  confidence: number;
  direction: 'up' | 'down' | 'stable';
}

interface InsightSuggestion {
  type: 'formula' | 'format' | 'validation' | 'automation';
  description: string;
  implementation: string;
  impact: 'low' | 'medium' | 'high';
}

interface CorrelationAnalysis {
  columns: [string, string];
  correlation: number;
  significance: number;
}

interface EnhancedSmartGridProps {
  entity?: string;
  workbookName?: string;
  onDataChange?: (workbook: SmartGridWorkbook) => void;
  onExport?: (format: string) => void;
  onShare?: () => void;
  onRunPlaybook?: () => void;
  enableAI?: boolean;
  enableCollaboration?: boolean;
  className?: string;
}

export const EnhancedSmartGrid: React.FC<EnhancedSmartGridProps> = ({
  entity = 'default',
  workbookName = 'workbook',
  onDataChange,
  onExport,
  onShare,
  onRunPlaybook,
  enableAI = true,
  enableCollaboration = true,
  className
}) => {
  // State management
  const [workbook, setWorkbook] = useState<SmartGridWorkbook | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [insights, setInsights] = useState<DataInsights | null>(null);
  const [showAI, setShowAI] = useState(false);
  const [showCollaboration, setShowCollaboration] = useState(false);
  const [editingCell, setEditingCell] = useState<{row: number, col: string} | null>(null);
  const [selectedRange, setSelectedRange] = useState<{start: CellReference, end: CellReference} | null>(null);

  // Refs
  const gridRef = useRef<HTMLDivElement>(null);
  const aiPanelRef = useRef<HTMLDivElement>(null);

  // Initialize workbook
  useEffect(() => {
    initializeWorkbook();
  }, [entity, workbookName]);

  const initializeWorkbook = async () => {
    try {
      setLoading(true);
      
      // Create default workbook structure
      const defaultWorkbook: SmartGridWorkbook = {
        sheets: new Map(),
        activeSheet: 'Sheet1',
        sharedFormulas: new Map(),
        crossSheetReferences: [],
        version: 1,
        lastModified: new Date().toISOString()
      };

      // Create default sheet
      const defaultSheet: SmartGridSheet = {
        id: 'Sheet1',
        name: 'Sheet1',
        rows: Array.from({ length: 100 }, (_, i) => ({
          id: `row-${i + 1}`,
          A: i === 0 ? 'Task' : `Task ${i}`,
          B: i === 0 ? 'Owner' : `Owner ${i}`,
          C: i === 0 ? 'Status' : 'Pending',
          D: i === 0 ? 'Due Date' : new Date(Date.now() + i * 86400000).toISOString().split('T')[0],
          E: i === 0 ? 'Cost' : Math.floor(Math.random() * 10000),
          F: i === 0 ? 'Total' : i > 0 ? `=SUM(E2:E${i + 1})` : ''
        })),
        columns: [
          { key: 'A', label: 'Task', id: 'A', header: 'Task', type: 'text', width: '200px', editable: true },
          { key: 'B', label: 'Owner', id: 'B', header: 'Owner', type: 'text', width: '150px', editable: true },
          { key: 'C', label: 'Status', id: 'C', header: 'Status', type: 'text', width: '120px', editable: true },
          { key: 'D', label: 'Due Date', id: 'D', header: 'Due Date', type: 'date', width: '120px', editable: true },
          { key: 'E', label: 'Cost', id: 'E', header: 'Cost', type: 'number', width: '100px', editable: true },
          { key: 'F', label: 'Total', id: 'F', header: 'Total', type: 'formula', width: '100px', editable: false }
        ],
        formulas: new Map(),
        validations: new Map(),
        conditionalFormats: [],
        metadata: {
          entity,
          lastModified: new Date().toISOString(),
          version: 1,
          owner: 'current-user',
          collaborators: [],
          permissions: []
        }
      };

      defaultWorkbook.sheets.set('Sheet1', defaultSheet);
      setWorkbook(defaultWorkbook);
      setActiveSheet('Sheet1');
      
      // Generate initial insights if AI is enabled
      if (enableAI) {
        await generateInsights(defaultSheet);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize workbook');
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = async (sheet: SmartGridSheet) => {
    try {
      // Simulate AI analysis
      const mockInsights: DataInsights = {
        anomalies: [
          {
            cellReference: 'E5',
            value: 8500,
            expectedRange: [1000, 5000],
            confidence: 0.95,
            description: 'Cost significantly higher than typical range'
          }
        ],
        trends: [
          {
            column: 'E',
            nextValues: [3200, 3400, 3600],
            confidence: 0.87,
            direction: 'up'
          }
        ],
        suggestions: [
          {
            type: 'formula',
            description: 'Add conditional formatting for overdue tasks',
            implementation: '=IF(D2<TODAY(), "Overdue", "On Track")',
            impact: 'medium'
          },
          {
            type: 'automation',
            description: 'Auto-assign tasks based on workload',
            implementation: 'Workflow: Auto-assign low-cost tasks to available team members',
            impact: 'high'
          }
        ],
        correlations: [
          {
            columns: ['E', 'D'],
            correlation: 0.72,
            significance: 0.01
          }
        ]
      };
      
      setInsights(mockInsights);
    } catch (err) {
      console.error('Failed to generate insights:', err);
    }
  };

  const handleCellEdit = useCallback((row: number, col: string, value: any) => {
    if (!workbook) return;
    
    const sheet = workbook.sheets.get(activeSheet);
    if (!sheet) return;

    const updatedRows = [...sheet.rows];
    if (updatedRows[row]) {
      updatedRows[row] = { ...updatedRows[row], [col]: value };
    }

    const updatedSheet = { ...sheet, rows: updatedRows };
    const updatedWorkbook = {
      ...workbook,
      sheets: new Map(workbook.sheets.set(activeSheet, updatedSheet)),
      lastModified: new Date().toISOString()
    };

    setWorkbook(updatedWorkbook);
    onDataChange?.(updatedWorkbook);
  }, [workbook, activeSheet, onDataChange]);

  const renderCell = useCallback((row: any, col: string, rowIndex: number) => {
    const value = row[col];
    const isEditing = editingCell?.row === rowIndex && editingCell?.col === col;
    const colIndex = currentSheet.columns.findIndex(column => column.id === col);
    const isSelected = !!selectedRange &&
      rowIndex >= Math.min(selectedRange.start.row, selectedRange.end.row) &&
      rowIndex <= Math.max(selectedRange.start.row, selectedRange.end.row) &&
      colIndex >= Math.min(selectedRange.start.column, selectedRange.end.column) &&
      colIndex <= Math.max(selectedRange.start.column, selectedRange.end.column);

    if (isEditing) {
      return (
        <Input
          value={value || ''}
          onChange={(e) => handleCellEdit(rowIndex, col, e.target.value)}
          onBlur={() => setEditingCell(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setEditingCell(null);
            }
          }}
          className="h-8 text-sm border-blue-500 focus:border-blue-400 bg-slate-800"
          autoFocus
        />
      );
    }

    return (
      <div
        className={cn(
          "h-8 flex items-center px-2 rounded cursor-pointer transition-all duration-200",
          isSelected && "bg-blue-500/20 border border-blue-500/50",
          "hover:bg-slate-700/50"
        )}
        onClick={() => setEditingCell({ row: rowIndex, col })}
        onDoubleClick={() => setEditingCell({ row: rowIndex, col })}
      >
        <span className="text-sm text-white truncate">
          {typeof value === 'number' ? value.toLocaleString() : value || ''}
        </span>
      </div>
    );
  }, [editingCell, selectedRange, handleCellEdit]);

  if (loading) {
    return (
      <div className={cn('space-y-6', className)}>
        <Card className="violett-card p-8">
          <div className="flex items-center justify-center gap-3 text-slate-300">
            <Loader2 className="animate-spin" size={20} />
            <span>Initializing Enhanced SmartGrid...</span>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('space-y-6', className)}>
        <Card className="violett-card p-8">
          <div className="text-center">
            <div className="text-red-400 mb-2 text-lg">⚠️ Error</div>
            <p className="text-slate-300">{error}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!workbook) {
    return (
      <div className={cn('space-y-6', className)}>
        <Card className="violett-card p-8">
          <div className="text-center text-slate-400">
            <div className="text-2xl mb-2">📭</div>
            <span>No workbook data available</span>
          </div>
        </Card>
      </div>
    );
  }

  const currentSheet = workbook.sheets.get(activeSheet);
  if (!currentSheet) return null;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="violett-card overflow-hidden">
          <div className="p-6 border-b border-slate-700/50 bg-slate-800/30">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">
                  Enhanced SmartGrid: {workbookName}
                </h2>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  <span>Active Sheet: {currentSheet.name}</span>
                  <span>Last modified: {new Date(workbook.lastModified).toLocaleString()}</span>
                  <span>Version {workbook.version}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {enableAI && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAI(!showAI)}
                    className="violett-button"
                  >
                    <Brain className="w-4 h-4 mr-2" />
                    AI Insights
                  </Button>
                )}
                {enableCollaboration && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCollaboration(!showCollaboration)}
                    className="violett-button"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Collaborate
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onExport?.('xlsx')}
                  className="violett-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onShare}
                  className="violett-button"
                >
                  <Share className="w-4 h-4 mr-2" />
                  Share
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRunPlaybook}
                  className="violett-button"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run Playbook
                </Button>
              </div>
            </div>

            {/* Sheet Tabs */}
            <div className="flex items-center gap-2 mb-4">
              {Array.from(workbook.sheets.keys()).map(sheetName => (
                <button
                  key={sheetName}
                  onClick={() => setActiveSheet(sheetName)}
                  className={cn(
                    "px-3 py-1 rounded text-sm transition-colors",
                    activeSheet === sheetName
                      ? "bg-blue-500 text-white"
                      : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                  )}
                >
                  {sheetName}
                </button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const newSheetName = `Sheet${workbook.sheets.size + 1}`;
                  const newSheet: SmartGridSheet = {
                    id: newSheetName,
                    name: newSheetName,
                    rows: Array.from({ length: 100 }, (_, i) => ({ id: `row-${i + 1}` })),
                    columns: currentSheet.columns,
                    formulas: new Map(),
                    validations: new Map(),
                    conditionalFormats: [],
                    metadata: {
                      ...currentSheet.metadata,
                      lastModified: new Date().toISOString()
                    }
                  };
                  const updatedWorkbook = {
                    ...workbook,
                    sheets: new Map(workbook.sheets.set(newSheetName, newSheet))
                  };
                  setWorkbook(updatedWorkbook);
                  setActiveSheet(newSheetName);
                }}
                className="text-slate-400 hover:text-white"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between">
              <div className="relative max-w-sm">
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  type="text"
                  placeholder="Search across all columns..."
                  className="violett-input pr-10 bg-slate-900/60 border-slate-600/40 text-white placeholder-slate-400 focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="violett-button"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Charts
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="violett-button"
                >
                  <Database className="w-4 h-4 mr-2" />
                  Data Sources
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="violett-button"
                >
                  <GitBranch className="w-4 h-4 mr-2" />
                  Version History
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="violett-button"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </div>
            </div>
          </div>

          {/* Grid */}
          <div 
            ref={gridRef}
            className="overflow-auto" 
            style={{ maxHeight: '600px' }}
          >
            <table className="w-full">
              <thead className="bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <tr className="border-b border-slate-700/50">
                  <th className="w-12 p-2 text-left text-sm font-semibold text-slate-200">
                    #
                  </th>
                  {currentSheet.columns.map(column => (
                    <th
                      key={column.id}
                      className={cn(
                        'p-2 text-left text-sm font-semibold text-slate-200 select-none',
                        column.width && `w-${column.width}`
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {column.header}
                        {column.type === 'formula' && <Calculator className="w-3 h-3 text-green-400" />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentSheet.rows.slice(0, 50).map((row, index) => (
                  <motion.tr
                    key={row.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.01 }}
                    className="border-b border-slate-700/30 hover:bg-slate-800/40 transition-all duration-200"
                  >
                    <td className="p-2 text-sm text-slate-400">
                      {index + 1}
                    </td>
                    {currentSheet.columns.map(column => (
                      <td key={column.id} className="p-1">
                        {renderCell(row, column.id, index)}
                      </td>
                    ))}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-slate-700/50 bg-slate-800/30">
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <span>Total Rows: <span className="text-white font-medium">{currentSheet.rows.length}</span></span>
              <span>Active Cells: <span className="text-blue-400 font-medium">{selectedRange ? 'Selected' : 'None'}</span></span>
              <span>Formulas: <span className="text-green-400 font-medium">{currentSheet.formulas.size}</span></span>
            </div>
            <div className="flex items-center gap-2">
              {insights?.suggestions.slice(0, 2).map((suggestion, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2 px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded text-blue-400 text-sm"
                >
                  <Zap className="w-3 h-3" />
                  {suggestion.description}
                </motion.div>
              ))}
            </div>
          </div>
        </Card>
      </motion.div>

      {/* AI Insights Panel */}
      <AnimatePresence>
        {showAI && insights && (
          <motion.div
            ref={aiPanelRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card className="violett-card">
              <div className="p-6 border-b border-slate-700/50 bg-slate-800/30">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Brain className="w-5 h-5 text-blue-400" />
                    AI Insights
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAI(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Anomalies */}
                  <div>
                    <h4 className="text-md font-semibold text-white mb-3">Anomalies Detected</h4>
                    <div className="space-y-2">
                      {insights.anomalies.map((anomaly, index) => (
                        <div key={index} className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                          <p className="text-sm text-red-400 font-medium">{anomaly.cellReference}</p>
                          <p className="text-xs text-slate-400">{anomaly.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Suggestions */}
                  <div>
                    <h4 className="text-md font-semibold text-white mb-3">AI Suggestions</h4>
                    <div className="space-y-2">
                      {insights.suggestions.map((suggestion, index) => (
                        <div key={index} className="p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                          <p className="text-sm text-blue-400 font-medium">{suggestion.type}</p>
                          <p className="text-xs text-slate-400">{suggestion.description}</p>
                          <p className="text-xs text-green-400 mt-1">Impact: {suggestion.impact}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EnhancedSmartGrid;
